/**
 * lib/store/persistence.ts
 *
 * Tiny localStorage wrapper that the Zustand store uses to write through after
 * every mutation. Uses a versioned key so seed-shape changes can be detected
 * and the store reset cleanly.
 *
 * SECURITY: Only fields in PERSIST_ALLOWLIST are written to localStorage.
 * This prevents PII (crew emails, phone numbers, passport data, chat messages)
 * from being persisted client-side.
 */
import * as Sentry from '@sentry/react';

// v2: BREAKING CHANGE — switched to allowlist-only persistence (no PII).
const STORAGE_KEY = 'rezolv-class-nextgen:v2';

/**
 * All localStorage keys this app has ever written.
 * Used by purgeLegacyKeys() and clearLocalStorage().
 */
const LEGACY_KEYS = [
  'rezolv-class-nextgen:v1', // Original key — contained full PII (crew, bookings, chat, etc.)
] as const;

/** All keys this app owns (legacy + current). */
const ALL_APP_KEYS = [...LEGACY_KEYS, STORAGE_KEY] as const;

/**
 * Module-level flag to prevent Sentry spam on repeated persistence failures.
 * Once a failure is reported, we skip subsequent reports for the session.
 * (saveToLocalStorage runs on 150ms debounce after every mutation.)
 */
let persistenceErrorReported = false;

/**
 * Explicit allowlist of state keys safe to persist to localStorage.
 * All other fields are excluded to prevent PII leakage.
 *
 * Safe fields:
 * - disruptionMode: boolean flag for ops mode (user's current session state)
 * - reportSchedules: schedule configs (no PII, just cron expressions)
 * - activeUserId: current user selection (user ID only, not the User object)
 * - timeMode: UTC / Local display toggle ('LOCAL' | 'UTC')
 * - piiMaskEnabled: Demo privacy mode switch (boolean, no PII)
 *
 * NOT persisted:
 * - systemConfig / branding: the tenant's configuration and identity. Always
 *   loaded fresh from the backend after login, so changes take effect
 *   immediately and nothing tenant-specific outlives the session.
 */
export const PERSIST_ALLOWLIST = [
  'disruptionMode',
  'reportSchedules',
  'activeUserId',
  'timeMode',
  'piiMaskEnabled',
] as const;

export type PersistAllowlistKey = (typeof PERSIST_ALLOWLIST)[number];

export interface PersistedShape {
  version: 2;
  state: Record<string, unknown>;
  hydratedAt: string;
}

/**
 * Extract only allowlisted fields from state for persistence.
 */
export function pickAllowlisted(state: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of PERSIST_ALLOWLIST) {
    if (key in state) {
      result[key] = state[key];
    }
  }
  return result;
}

export function loadFromLocalStorage<T>(): T | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedShape;
    if (parsed.version !== 2) return null; // Ignore v1 (contained PII)
    return parsed.state as T;
  } catch {
    return null;
  }
}

export function saveToLocalStorage(state: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  try {
    // Only persist allowlisted fields — no PII
    const safeState = pickAllowlisted(state);
    const payload: PersistedShape = {
      version: 2,
      state: safeState,
      hydratedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (err) {
    // Log persistence failures to Sentry ONCE per session (quota exceeded, etc.)
    // Skip if already reported — saveToLocalStorage runs on every mutation.
    if (!persistenceErrorReported) {
      persistenceErrorReported = true;
      Sentry.captureException(err, {
        tags: { component: 'persistence' },
        extra: {
          storageKey: STORAGE_KEY,
          payloadKeys: Object.keys(pickAllowlisted(state)),
        },
      });
    }
    // Graceful degradation — app continues without persistence
  }
}

/**
 * Remove all localStorage keys this app owns (current + legacy).
 * Called on logout to ensure no stale data remains.
 */
export function clearLocalStorage(): void {
  if (typeof window === 'undefined') return;
  for (const key of ALL_APP_KEYS) {
    window.localStorage.removeItem(key);
  }
}

/**
 * Purge legacy localStorage keys that contained PII.
 * Must be called ONCE on app start, BEFORE hydration.
 * This ensures existing users' v1 data (crew emails, chat, etc.) is deleted.
 */
export function purgeLegacyKeys(): void {
  if (typeof window === 'undefined') return;
  for (const key of LEGACY_KEYS) {
    window.localStorage.removeItem(key);
  }
}
